/**
 * What is still to be done on a project, gathered from the tabs that track it.
 *
 * The Tasks tab used to list only tasks somebody typed in, so a project with
 * blank lease questions, unfinished milestones and a plan set waiting on review
 * read "No tasks here". This collects those into one list. It reads, it never
 * creates: each item points at the tab where the work is actually done, and
 * nothing here is a second place to record it.
 *
 * Each item is `{ id, source, title, detail, dueDate, overdue, tab, href }`.
 * `tab` is a ProjectDetail tab value; `href` is set instead for a plan, whose
 * review has its own page.
 */
import { TENANT_LEASE_FIELDS, isAnswered, isYes, tenantDetailsStatus } from '@/lib/tenantLeaseDetails';
import { isPastDue } from '@/lib/planPackage';

/* A plan in one of these is waiting on somebody. Approved and rejected are
   decided, and a draft has not been sent. */
const PLAN_WAITING = {
  in_review: 'Waiting on review',
  changes_requested: 'Changes requested - resubmit',
  revisions_required: 'Revise and resubmit',
};

/** The lease questions still blank, by label, counted the way the tab counts them. */
export function blankLeaseFields(project) {
  if (project?.type !== 'tenant') return [];
  return TENANT_LEASE_FIELDS
    .filter(f => !f.optional && (!f.dependsOn || isYes(project?.[f.dependsOn])))
    .filter(f => !isAnswered(project, f))
    .map(f => f.label);
}

export function openItems({ project, milestones = [], plans = [] } = {}) {
  const items = [];

  const blanks = blankLeaseFields(project);
  if (blanks.length) {
    const status = tenantDetailsStatus(project);
    items.push({
      id: 'lease-details',
      source: 'Lease Details',
      title: `Answer ${blanks.length} lease question${blanks.length === 1 ? '' : 's'}`,
      detail: blanks.join(', '),
      dueDate: status.dueDate || '',
      overdue: status.overdue,
      tab: 'lease',
    });
  }

  for (const m of milestones) {
    if (m.approval_status === 'pending_review') {
      items.push({
        id: `milestone-${m.id}`,
        source: 'Milestone',
        title: m.name,
        detail: 'Waiting on Construction Manager approval',
        dueDate: m.planned_finish || '',
        overdue: false,
        tab: 'milestones',
      });
    } else if (m.status !== 'complete') {
      items.push({
        id: `milestone-${m.id}`,
        source: 'Milestone',
        title: m.name,
        detail: m.approval_status === 'revisions_required'
          ? 'Revisions required by the Construction Manager'
          : m.status === 'blocked' ? 'Blocked' : m.status === 'in_progress' ? 'In progress' : 'Not started',
        dueDate: m.planned_finish || '',
        overdue: isPastDue(m.planned_finish),
        tab: 'milestones',
      });
    }
  }

  for (const p of plans) {
    const waiting = PLAN_WAITING[p.status];
    if (!waiting) continue;
    items.push({
      id: `plan-${p.id}`,
      source: p.discipline === 'signage' ? 'Signage' : 'Plans',
      title: p.plan_set_name || 'Plan submission',
      detail: waiting,
      dueDate: p.status === 'in_review' ? (p.due_date || '') : '',
      overdue: p.status === 'in_review' && isPastDue(p.due_date),
      href: `/PlanReview?id=${p.id}`,
    });
  }

  /* Overdue first, then by date, undated last - the same order a person
     would work them in. */
  return items.sort((a, b) =>
    (b.overdue - a.overdue)
    || (!a.dueDate - !b.dueDate)
    || String(a.dueDate).localeCompare(String(b.dueDate)));
}

/** Hand-added and workflow tasks not yet done - the old standard lease rows excluded. */
export function openTasks(tasks = []) {
  return tasks.filter(t => !t.is_standard_task && t.status !== 'complete');
}

/** The number on the Tasks tab: everything above that still needs doing. */
export function openItemCount({ project, milestones, plans, tasks }) {
  return openItems({ project, milestones, plans }).length + openTasks(tasks).length;
}
